// src/pages/ForgotPassword.tsx
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const APP_URL =
    (process.env.REACT_APP_APP_URL as string | undefined) ||
    window.location.origin;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError("Por favor ingresa tu correo.");
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: `${APP_URL}/update-password`,
    });

    setLoading(false);

    if (error) {
      console.error("resetPasswordForEmail error:", error);
      setError("No se pudo enviar el correo. Revisa el email o inténtalo más tarde.");
      return;
    }

    // ✅ Mensaje “seguro” (no revela si existe o no existe el correo)
    setMessage(
      "Si ese correo existe en el sistema, te llegará un enlace para restablecer la contraseña. Revisa Inbox y Spam/Promociones."
    );
  };

  return (
    <div className="relative min-h-screen bg-[#0b0b0f] flex items-center justify-center px-6 text-white overflow-hidden">
      {/* Glow background (igual a Home/Login/Register) */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="w-full max-w-md">
        {/* Volver al inicio */}
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition"
          type="button"
        >
          <span aria-hidden>←</span> Volver al inicio
        </button>

        <div className="w-full bg-[#111114]/80 p-10 rounded-2xl border border-white/10 shadow-2xl backdrop-blur-sm">
          {/* Header marca (sin “Recuperar contraseña” grande) */}
          <div className="text-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              Ovylon
              <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
            </div>

            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
            </p>
          </div>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs text-gray-400 mb-2">Correo electrónico</label>
              <input
                type="email"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-gray-200 focus:ring-2 focus:ring-white/20 outline-none"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            {message && <p className="text-emerald-300 text-sm text-center">{message}</p>}

            {/* Botón principal (igual a Login/Register) */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Enviando..." : "Enviar enlace"}
            </button>
          </form>

          <p className="mt-6 text-center text-gray-400 text-sm">
            ¿Recordaste tu contraseña?{" "}
            <Link to="/login" className="text-white font-semibold hover:underline">
              Volver a iniciar sesión
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          Si no ves el correo, revisa Spam/Promociones y espera 1–2 minutos.
        </p>
      </div>
    </div>
  );
}
